import { cn } from '../../lib/utils';
import { MARKETS } from '../../constants/markets';
import { Star, Search, X } from 'lucide-react';

interface MarketsPanelProps {
  showMarketsPanel: boolean;
  setShowMarketsPanel: (show: boolean) => void;
  favorites: string[];
  activeMarketCategory: string;
  setActiveMarketCategory: (category: string) => void;
  symbol: string;
  setSymbol: (symbol: string) => void;
  setUseRealData: (useRealData: boolean) => void;
  toggleFavorite: (marketSymbol: string) => void;
  user: any;
}

export function MarketsPanel({
  showMarketsPanel,
  setShowMarketsPanel,
  favorites,
  activeMarketCategory,
  setActiveMarketCategory,
  symbol,
  setSymbol,
  setUseRealData,
  toggleFavorite,
  user,
}: MarketsPanelProps) {
  if (!showMarketsPanel) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300 pointer-events-auto"
      onClick={() => setShowMarketsPanel(false)}
    >
      <div 
        className="bg-[#060919]/90 backdrop-blur-xl border border-[#00d9ff]/30 p-5 md:p-6 rounded-2xl w-full max-w-[420px] max-h-[85vh] overflow-y-auto overflow-x-hidden transition-all duration-300 custom-scrollbar shadow-[0_0_50px_rgba(0,0,0,0.8)] animate-in fade-in zoom-in-95 flex flex-col"
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-[#060919] pt-1 pb-3 z-10 border-b border-[#00d9ff]/20">
          <h2 className="text-[14px] font-bold uppercase tracking-[3px] text-[#00d9ff] flex items-center gap-2">
            <Search size={16} /> Mercados
          </h2>
          <button onClick={() => setShowMarketsPanel(false)} className="text-[#00d9ff] p-1.5 bg-[#00d9ff]/10 rounded-full hover:bg-[#00d9ff]/20 transition-all">
            <X size={18} />
          </button>
        </div>

        {/* Category selection */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-4 sticky top-[48px] bg-[#060919] z-10 custom-scrollbar no-scrollbar">
          <button
            onClick={() => setActiveMarketCategory('Favoritos')}
            className={cn(
              "px-3 py-1.5 rounded-full text-[9px] uppercase tracking-[1px] font-mono transition-all duration-200 shrink-0 border flex items-center gap-1 cursor-pointer",
              activeMarketCategory === 'Favoritos'
                ? "bg-[#ffcc00]/20 border-[#ffcc00] text-[#ffcc00] font-bold"
                : "bg-transparent border-transparent text-[#666666] hover:text-[#00d9ff]"
            )}
          >
            <Star size={10} className={favorites.length > 0 ? "fill-[#ffcc00] text-[#ffcc00]" : ""} />
            Favoritos
          </button>
          {Object.keys(MARKETS).map((category) => (
            <button
              key={category}
              onClick={() => setActiveMarketCategory(category)}
              className={cn(
                "px-3 py-1.5 rounded-full text-[9px] uppercase tracking-[1px] font-mono transition-all duration-200 shrink-0 border cursor-pointer",
                activeMarketCategory === category
                  ? "bg-[#00d9ff]/20 border-[#00d9ff] text-[#00d9ff] font-bold"
                  : "bg-transparent border-transparent text-[#666666] hover:text-[#00d9ff]"
              )}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Market list */}
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
          {(() => {
            let listToRender: { symbol: string; name: string; category?: string }[] = [];
            if (activeMarketCategory === 'Favoritos') {
              Object.entries(MARKETS).forEach(([cat, items]) => {
                items.forEach((item) => {
                  const currentFavs = Array.isArray(favorites) ? favorites : [];
                  if (currentFavs.includes(item.symbol)) {
                    listToRender.push({ ...item, category: cat });
                  }
                });
              });
            } else {
              listToRender = MARKETS[activeMarketCategory as keyof typeof MARKETS] || [];
            }

            if (listToRender.length === 0) {
              return (
                <div className="text-center py-8 text-[#666666] text-[11px] font-mono uppercase tracking-[1px]">
                  {activeMarketCategory === 'Favoritos' ? "Nenhum mercado favorito." : "Nenhum ativo disponível nesta categoria."}
                </div>
              );
            }

            return listToRender.map((market) => {
              const currentFavs = Array.isArray(favorites) ? favorites : [];
              const isFav = currentFavs.includes(market.symbol);
              const isCurrent = symbol === market.symbol;

              return (
                <div
                  key={market.symbol}
                  onClick={() => {
                    setSymbol(market.symbol);
                    setUseRealData(true);
                    setShowMarketsPanel(false);
                  }}
                  className={cn(
                    "flex justify-between items-center p-3 rounded-xl border transition-all duration-200 cursor-pointer",
                    isCurrent 
                      ? "bg-[#00d9ff]/10 border-[#00d9ff]/50 shadow-[0_0_10px_rgba(0,217,255,0.15)]"
                      : "bg-[#0a0e27]/40 border-transparent hover:bg-[#0a0e27]/80 hover:border-[#00d9ff]/20"
                  )}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-white text-xs font-bold font-mono tracking-wider">
                      {market.symbol}
                    </span>
                    <span className="text-[#666666] text-[10px]">
                      {market.name} {market.category && `(${market.category})`}
                    </span>
                  </div>

                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleFavorite(market.symbol)}
                      className={cn(
                        "p-2 rounded-full transition-all duration-200 hover:bg-white/5 cursor-pointer",
                        isFav ? "text-[#ffcc00]" : "text-[#666666] hover:text-[#ffcc00]"
                      )}
                      title={isFav ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                    >
                      <Star size={14} className={isFav ? "fill-[#ffcc00]" : ""} />
                    </button>
                    <button
                      onClick={() => {
                        setSymbol(market.symbol);
                        setUseRealData(true);
                        setShowMarketsPanel(false);
                      }}
                      className={cn(
                        "px-2.5 py-1 rounded-lg text-[9px] uppercase tracking-[1px] font-mono transition-all duration-200 cursor-pointer",
                        isCurrent
                          ? "bg-[#00d9ff]/20 text-[#00d9ff] font-extrabold border border-[#00d9ff]/30"
                          : "bg-[#0a0e27] text-[#666666] border border-transparent hover:border-[#00d9ff]/30 hover:text-[#00d9ff]"
                      )}
                    >
                      {isCurrent ? "Ativo" : "Ver"}
                    </button>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Save notice / prompt for guest */}
        <div className="mt-4 pt-3 border-t border-[#00d9ff]/10 text-center flex flex-col gap-1">
          <span className="text-[8px] uppercase tracking-[1.5px] text-[#666666] font-mono">
            {user ? "Sincronizado na Nuvem" : "Favoritos salvos localmente"}
          </span>
          {!user && (
            <span className="text-[7px] uppercase tracking-[1px] text-[#00d9ff]/60 font-mono">
              Faça login para salvar seus favoritos em sua conta
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
